import React, { useState, useCallback, useRef } from 'react';
import AmazonAdsAgreementLink from '@features/combine-accounts/components/AmazonAdsAgreementLink';
import { useNavigate } from 'react-router-dom';
import { Card } from '@amzn/storm-ui';
import useIntl from '@i18n/useIntl';
import { CombineAccountType } from 'src/shared/services/combine-accounts/types';
import {
  useLazyValidateAccountCombinationQuery,
  useCreateAccountCombinationMutation,
} from 'src/shared/services/combine-accounts/combineAccounts';
import CombineAccountsLayout from 'src/shared/features/combine-accounts/components/CombineAccountsLayout';
import CombineAccountsActionButtons from 'src/shared/features/combine-accounts/components/CombineAccountsActionButtons';
import {
  COMBINE_ACCOUNTS_LANDING_PATH,
  COMBINE_ACCOUNTS_DETAILS_PATH,
} from 'src/shared/routes';
import {
  COMBINE_ACCOUNTS_ERROR_MAX_ACCOUNTS_KEY,
  COMBINE_ACCOUNTS_ERROR_DUPLICATE_DSP_KEY,
  COMBINE_ACCOUNTS_ERROR_DUPLICATE_SA_KEY,
  COMBINE_ACCOUNTS_ERROR_INELIGIBLE_KEY,
  COMBINE_ACCOUNTS_ERROR_INCOMPATIBLE_KEY,
  COMBINE_ACCOUNTS_ERROR_NOT_ELIGIBLE_KEY,
  COMBINE_ACCOUNTS_ERROR_VALIDATION_FAILED_KEY,
  COMBINE_ACCOUNTS_REQUEST_SUBMIT_ERROR_KEY,
  COMBINE_ACCOUNTS_REQUEST_SUBMITTED_HEADER_KEY,
  COMBINE_ACCOUNTS_REQUEST_SUBMITTED_BODY_KEY,
  COMBINE_ACCOUNTS_REQUEST_SUBMITTED_EMAIL_NOTE_KEY,
  COMBINE_ACCOUNTS_DONE_KEY,
  COMBINE_ACCOUNTS_CANCEL_KEY,
  COMBINE_ACCOUNTS_CONFIRM_HEADER_KEY,
  COMBINE_ACCOUNTS_CONFIRM_BODY_KEY,
  COMBINE_ACCOUNTS_CONFIRM_BULLET_CAMPAIGNS_KEY,
  COMBINE_ACCOUNTS_CONFIRM_BULLET_USERS_KEY,
  COMBINE_ACCOUNTS_CONFIRM_BULLET_FEATURES_KEY,
  COMBINE_ACCOUNTS_CONFIRM_BULLET_MANAGE_KEY,
  COMBINE_ACCOUNTS_CONFIRM_CHECKBOX_KEY,
  COMBINE_ACCOUNTS_CONFIRM_SUBMIT_KEY,
  CHOOSE_ACCOUNTS_DESCRIPTION_KEY,
  CHOOSE_ACCOUNTS_REQUEST_ACCESS_INFO_KEY,
} from '@features/combine-accounts/constants';
import { isWeblabLaunched } from 'src/shared/weblab/weblabUtils';
import { Weblab } from 'src/shared/weblab/enum';
import { AccountItem } from './types';
import ChooseAccountsStep from './components/ChooseAccountsStep';
import ConfirmationModal from './components/ConfirmationModal';

/** Checks whether a newly added account is ineligible for combination. */
const isIneligibleForCombination = (
  item: AccountItem | undefined,
): item is AccountItem =>
  !!item &&
  !item.isPendingAccess &&
  !!item.eligibilityStatus &&
  !item.eligibilityStatus.isEligible;

/**
 * The one GRS validation error code with its own copy. Every other rejection is
 * shown as the generic not-eligible message; the specific codes (access,
 * already-globalized, not-found, billing) are not actionable from this page.
 */
const VALIDATION_ERROR_CODE_INCOMPATIBLE = 'INCOMPATIBLE_SELLING_RELATIONSHIP';

const AccountCombinationChoosePage: React.FC = () => {
  const navigate = useNavigate();
  const { intl } = useIntl();
  // The "Request access" tab is shown only when the COMBINE_REQUEST_ACCESS
  // weblab is T1; C or a missing/failed treatment hides it. Allocated by
  // customer ID.
  const isRequestAccessHidden = !isWeblabLaunched(
    Weblab.COMBINE_REQUEST_ACCESS,
  );
  // The request-access sentence is appended to the choose-accounts description
  // only when the Request access tab is available (weblab T1), so a customer
  // who cannot request access is not told to.
  const chooseAccountsDescription = isRequestAccessHidden
    ? intl.getMessage(CHOOSE_ACCOUNTS_DESCRIPTION_KEY)
    : `${intl.getMessage(CHOOSE_ACCOUNTS_DESCRIPTION_KEY)} ${intl.getMessage(
        CHOOSE_ACCOUNTS_REQUEST_ACCESS_INFO_KEY,
      )}`;
  const [selectedItems, setSelectedItems] = useState<AccountItem[]>([]);
  const [errorAlert, setErrorAlert] = useState<{
    message: string;
    targetId: string;
  } | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [submitPopoverError, setSubmitPopoverError] = useState<string | null>(
    null,
  );
  const submitButtonRef = useRef<HTMLDivElement>(null);

  const [validateCombination] = useLazyValidateAccountCombinationQuery();
  const [createCombination] = useCreateAccountCombinationMutation();
  // Guards against a double-submit firing the create mutation twice (which
  // would create duplicate invitations) across the async validate+create window.
  const isSubmittingRef = useRef(false);
  // Drives the submit button's spinner. This mirrors isSubmittingRef's lifetime
  // rather than OR-ing the two mutations' own loading flags, because those cover
  // only their individual requests: between validate resolving and create being
  // dispatched both read false for a render, which made the spinner flicker out
  // and back mid-submit. The ref stays as the synchronous guard, since state
  // updates are asynchronous and a fast double click could otherwise slip past.
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasPendingAccess = selectedItems.some((i) => i.isPendingAccess);

  const selectedItemsRef = useRef(selectedItems);
  selectedItemsRef.current = selectedItems;

  const handleSelectionChange = useCallback(
    (items: AccountItem[]) => {
      // Previously selected items before this change
      const prev = selectedItemsRef.current;
      // The newly added item (if any) — undefined when an item was removed
      const added = items.find(
        (item) => !prev.some((selected) => selected.id === item.id),
      );

      if (items.length > 2) {
        if (added) {
          setErrorAlert({
            message: intl.getMessage(COMBINE_ACCOUNTS_ERROR_MAX_ACCOUNTS_KEY),
            targetId: String(added.id),
          });
        }
        setSelectedItems([...prev]);
        return;
      }
      if (
        added &&
        prev.some((selected) => selected.accountType === added.accountType)
      ) {
        const errorKey =
          added.accountType === CombineAccountType.DSP
            ? COMBINE_ACCOUNTS_ERROR_DUPLICATE_DSP_KEY
            : COMBINE_ACCOUNTS_ERROR_DUPLICATE_SA_KEY;
        setErrorAlert({
          message: intl.getMessage(errorKey),
          targetId: String(added.id),
        });
        setSelectedItems([...prev]);
        return;
      }
      if (added && isIneligibleForCombination(added)) {
        setErrorAlert({
          message: intl.formatMessage(COMBINE_ACCOUNTS_ERROR_INELIGIBLE_KEY, {
            reasonCode: added.eligibilityStatus!.reasonCode ?? 'unknown reason',
          }),
          targetId: String(added.id),
        });
        setSelectedItems([...prev]);
        return;
      }
      setSelectedItems(items);
      setErrorAlert(null);
    },
    [intl],
  );

  const handleAddRequestAccessItem = useCallback((item: AccountItem) => {
    setSelectedItems((p) => [...p, item]);
    setErrorAlert(null);
  }, []);

  /** Copy for a validate response with `eligible: false`, chosen by GRS error code. */
  const validationErrorMessage = (
    errorCode: string | null | undefined,
  ): string =>
    errorCode === VALIDATION_ERROR_CODE_INCOMPATIBLE
      ? intl.getMessage(COMBINE_ACCOUNTS_ERROR_INCOMPATIBLE_KEY)
      : intl.getMessage(COMBINE_ACCOUNTS_ERROR_NOT_ELIGIBLE_KEY);

  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;

    const sa = selectedItems.find(
      (i) => i.accountType === CombineAccountType.SA,
    );
    const dsp = selectedItems.find(
      (i) => i.accountType === CombineAccountType.DSP,
    );

    if (!sa || !dsp) return;

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      try {
        const result = await validateCombination({
          globalAccountId: String(sa.id),
          cfid: String(dsp.id),
        }).unwrap();

        if (!result.eligible) {
          setSubmitPopoverError(validationErrorMessage(result.errorCode));
          return;
        }
      } catch {
        setSubmitPopoverError(
          intl.getMessage(COMBINE_ACCOUNTS_ERROR_VALIDATION_FAILED_KEY),
        );
        return;
      }

      if (hasPendingAccess) {
        // Request-access path: create the combination invitation. The backend
        // returns a pending invitation because the requester lacks admin access
        // on one of the accounts. Only surface the "Request submitted" modal
        // once the invitation is actually created.
        try {
          await createCombination({
            dspAccountId: String(dsp.id),
            sponsoredAdsAccountId: String(sa.id),
          }).unwrap();
          setShowConfirmModal(true);
        } catch {
          setSubmitPopoverError(
            intl.getMessage(COMBINE_ACCOUNTS_REQUEST_SUBMIT_ERROR_KEY),
          );
        }
      } else {
        const params = new URLSearchParams();
        params.set('ga', String(sa.id));
        params.set('cfid', String(dsp.id));
        navigate(`${COMBINE_ACCOUNTS_DETAILS_PATH}?${params.toString()}`);
      }
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(COMBINE_ACCOUNTS_LANDING_PATH);
  };

  return (
    <CombineAccountsLayout
      onClose={handleCancel}
      actionButtons={
        <CombineAccountsActionButtons
          onCancel={handleCancel}
          onSubmit={handleSubmit}
          submitRef={submitButtonRef}
          popoverError={submitPopoverError}
          onPopoverClose={() => setSubmitPopoverError(null)}
          submitLoading={isSubmitting}
          submitDisabled={
            isSubmitting ||
            !selectedItems.some(
              (i) => i.accountType === CombineAccountType.DSP,
            ) ||
            !selectedItems.some((i) => i.accountType === CombineAccountType.SA)
          }
        />
      }
    >
      <Card>
        <ChooseAccountsStep
          selectedItems={selectedItems}
          onSelectionChange={handleSelectionChange}
          onAddRequestAccessItem={handleAddRequestAccessItem}
          errorAlert={errorAlert}
          onError={setErrorAlert}
          onClearError={() => setErrorAlert(null)}
          maxSelections={2}
          hideRequestAccessTab={isRequestAccessHidden}
          description={chooseAccountsDescription}
        />
      </Card>

      <ConfirmationModal
        isOpen={showConfirmModal}
        hasPendingAccess={hasPendingAccess}
        selectedItems={selectedItems}
        confirmChecked={confirmChecked}
        onConfirmCheckedChange={setConfirmChecked}
        pendingHeader={intl.getMessage(
          COMBINE_ACCOUNTS_REQUEST_SUBMITTED_HEADER_KEY,
        )}
        pendingBodyText={intl.getMessage(
          COMBINE_ACCOUNTS_REQUEST_SUBMITTED_BODY_KEY,
        )}
        pendingEmailNote={intl.getMessage(
          COMBINE_ACCOUNTS_REQUEST_SUBMITTED_EMAIL_NOTE_KEY,
        )}
        doneLabel={intl.getMessage(COMBINE_ACCOUNTS_DONE_KEY)}
        header={intl.getMessage(COMBINE_ACCOUNTS_CONFIRM_HEADER_KEY)}
        bodyText={intl.getMessage(COMBINE_ACCOUNTS_CONFIRM_BODY_KEY)}
        bullets={[
          intl.getMessage(COMBINE_ACCOUNTS_CONFIRM_BULLET_CAMPAIGNS_KEY),
          intl.getMessage(COMBINE_ACCOUNTS_CONFIRM_BULLET_USERS_KEY),
          intl.getMessage(COMBINE_ACCOUNTS_CONFIRM_BULLET_FEATURES_KEY),
          intl.getMessage(COMBINE_ACCOUNTS_CONFIRM_BULLET_MANAGE_KEY),
        ]}
        checkboxLabel={intl.formatMessage(
          COMBINE_ACCOUNTS_CONFIRM_CHECKBOX_KEY,
          { agreement: <AmazonAdsAgreementLink /> },
        )}
        submitLabel={intl.getMessage(COMBINE_ACCOUNTS_CONFIRM_SUBMIT_KEY)}
        cancelLabel={intl.getMessage(COMBINE_ACCOUNTS_CANCEL_KEY)}
        onClose={() => {
          setShowConfirmModal(false);
          setConfirmChecked(false);
          // The pending-access modal is a terminal "Request submitted" state.
          // Clear the selection on dismissal so submitting again can't create a
          // duplicate invitation for the same accounts.
          if (hasPendingAccess) {
            setSelectedItems([]);
            setErrorAlert(null);
          }
        }}
        onConfirm={() => {
          setShowConfirmModal(false);
        }}
      />
    </CombineAccountsLayout>
  );
};

export default AccountCombinationChoosePage;
